import React, {useCallback, useEffect, useMemo, useState} from "react";
import {execFn} from "../../../common/generic-react.functions.js";
import Icon from "../../atoms/icon/icon.component.js";
import Pagination from "../../atoms/pagination/pagination.component.js";
const DEFAULT_OPTIONS = {
  page: 1,
  itemsPerPage: 50,
  itemsTotal: 0
};
export function useDataTableParsers({
  headers,
  options,
  items,
  onPaginate
}) {
  const MAIN_DEFAULT_OPTIONS = useMemo(() => ({
    ...DEFAULT_OPTIONS
  }), []);
  const parseHeaders = useCallback((heads) => heads.reduce((a, {
    value,
    ...props
  }) => ({
    ...a,
    [value]: props
  }), {}), []);
  const [headerInfo, setHeaderInfo] = useState(() => parseHeaders(headers));
  useEffect(() => {
    setHeaderInfo(() => parseHeaders(headers));
  }, [headers, parseHeaders]);
  const transformSortToObj = useCallback((sortBy) => {
    if (!sortBy || !Array.isArray(sortBy)) {
      return sortBy;
    }
    if (Array.isArray(sortBy)) {
      return sortBy.reduce((a, s) => ({
        ...a,
        [s]: "ASC"
      }), {});
    }
    return void 0;
  }, []);
  const getParsedOptions = useCallback(() => {
    const opts = {
      ...MAIN_DEFAULT_OPTIONS,
      ...options
    };
    return {
      ...opts,
      sortBy: transformSortToObj(opts?.sortBy)
    };
  }, [MAIN_DEFAULT_OPTIONS, options, transformSortToObj]);
  const [parsedOptions, setParsedOptions] = useState(() => getParsedOptions());
  useEffect(() => {
    setParsedOptions(() => getParsedOptions());
  }, [getParsedOptions, options]);
  const filterItems = useCallback((its, filterBy) => {
    if (!filterBy || !Object.keys(filterBy).length) {
      return its;
    }
    const entries = Object.entries(filterBy);
    return its.filter((it) => {
      for (let i = 0; i < entries.length; i++) {
        const [k, v] = entries[+i];
        if (k && v && it[k.toString()] && !it[k.toString()].toString().toLowerCase().includes(v.toString().toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, []);
  const sortItemsBy = useCallback((its, value, type = "ASC") => {
    const sortFn = headers.find((h) => h.value === value)?.sort;
    if (sortFn) {
      its.sort(sortFn);
    } else {
      its.sort((a, b) => {
        const aV = a[value.toString()];
        const bV = b[value.toString()];
        if (typeof bV === "number" && typeof aV === "number") {
          return type === "ASC" ? aV - bV : bV - aV;
        } else if (typeof bV === "string" && typeof aV === "string") {
          return type === "ASC" ? aV.toLowerCase().localeCompare(bV.toLowerCase()) : bV.toLowerCase().localeCompare(aV.toLowerCase());
        }
        return 0;
      });
    }
    return its;
  }, [headers]);
  const sortItems = useCallback((its, sortBy) => {
    const sort = transformSortToObj(sortBy);
    if (!sort) {
      return its;
    }
    let result = [...its];
    Object.entries(sort).forEach(([k, type]) => {
      result = sortItemsBy(result, k, type);
    });
    return result;
  }, [sortItemsBy, transformSortToObj]);
  const getItems = useCallback(() => {
    const filtered = filterItems(items, parsedOptions?.filterBy);
    return sortItems(filtered, parsedOptions?.sortBy);
  }, [filterItems, items, parsedOptions?.filterBy, parsedOptions?.sortBy, sortItems]);
  const [parsedItems, setParsedItems] = useState(() => getItems());
  useEffect(() => {
    setParsedItems(() => getItems());
  }, [getItems, parsedOptions]);
  const getAriaSortText = useCallback((value) => {
    const obj = transformSortToObj(parsedOptions?.sortBy);
    switch (obj && obj[value.toString()]) {
      case "ASC":
        return "ascending";
      case "DESC":
        return "descending";
      case void 0:
      default:
        return "none";
    }
  }, [parsedOptions?.sortBy, transformSortToObj]);
  const sortInfo = useCallback((value) => {
    const {
      sortable
    } = headerInfo[value.toString()];
    const sortText = getAriaSortText(value);
    let sortIcon = null;
    const sortCls = sortText !== "none" ? " data-table__header--sorted" : "";
    if (sortable) {
      const upIcon = "caret-square-up";
      const downIcon = "caret-square-down";
      const color = sortText !== "none" ? "#9ca3af" : "#e5e7eb";
      const iconName = sortText === "descending" ? downIcon : upIcon;
      sortIcon = /* @__PURE__ */ React.createElement(Icon, {
        className: "data-table__header__sorter",
        name: iconName,
        color
      });
    }
    return [sortText, sortIcon, sortCls];
  }, [getAriaSortText, headerInfo]);
  const sortColumn = useCallback((value) => {
    setParsedOptions((opts) => {
      const sort = opts?.sortBy && opts?.sortBy[value.toString()];
      let newSort;
      switch (sort) {
        case "ASC":
          newSort = "DESC";
          break;
        case "DESC":
          newSort = void 0;
          break;
        case void 0:
        default:
          newSort = "ASC";
      }
      const result = {
        ...opts,
        sortBy: {
          [value]: newSort
        }
      };
      if (newSort === void 0 && result.sortBy) {
        delete result.sortBy[value.toString()];
      }
      return result;
    });
  }, []);
  const paginateEvent = useCallback((page) => {
    execFn(onPaginate, page);
  }, [onPaginate]);
  const paginationElement = useMemo(() => options?.itemsPerPage && options?.itemsTotal ? /* @__PURE__ */ React.createElement(Pagination, {
    onChange: paginateEvent,
    length: Math.round(options.itemsTotal / options.itemsPerPage),
    activePage: options?.page
  }) : null, [options?.itemsPerPage, options?.itemsTotal, options?.page, paginateEvent]);
  return {
    headerInfo: [headerInfo, setHeaderInfo],
    parsedOptions: [parsedOptions, setParsedOptions],
    parsedItems: [parsedItems, setParsedItems],
    sortInfo,
    sortColumn,
    paginationElement
  };
}
